<template>
  <nav class="task-nav">
    <div class="nav-panel">
      <div
          v-for="item in items"
          :key="item.key"
          class="nav-item"
          :class="{ 'is-active': active === item.key }"
          @click="onClick(item.key)"
      >
        {{ item.label }}
      </div>
    </div>

    <div class="nav-right">

      <div class="back-btn" @click="goBack" title="返回">
        <span class="arrow">↩</span>
      </div>

      <div
        class="user-menu"
        @mouseenter="menuOpen = true"
        @mouseleave="menuOpen = false"
      >
        <div class="user-box" @click="openMenu">
          <div class="avatar-wrap" @click.stop="onAvatarClick">
            <img
              v-if="avatarSrc && !avatarError"
              class="nav-avatar"
              :src="avatarSrc"
              alt="avatar"
              @error="avatarError = true"
            >
            <div v-else class="nav-avatar">{{ nicknameFirst }}</div>
            <div class="avatar-mask">上传头像</div>
          </div>
          <input
            ref="avatarInput"
            type="file"
            accept="image/*"
            style="display:none"
            @change="onAvatarChange"
          >
        </div>

        <div class="dropdown" :class="{ open: menuOpen }">
          <div class="dropdown-item" @click="goProfile">
            个人中心
          </div>
          <div class="dropdown-item logout" @click="logout">
            退出登录
          </div>
        </div>
      </div>

    </div>
  </nav>
</template>


<script>
import router from '@/router'
import { resolveAvatarUrl } from '@/utils/avatar.js'
import { profileState, saveProfile, syncUserLocalStorage, resetProfileState } from './profileStorage'
import { uploadAvatar, updateAvatar, getMe, logout as logoutApi } from '@/api/user'

export default {
  name:'TaskNav',
  props:{
    active:{
      type:String,
      default:'home'
    }
  },
  data(){
    return {
      items:[
        {key:'home',label:'时间表'},
        {key:'thoughts',label:'随想'},
        {key:'friends',label:'朋友'}
      ],
      menuOpen: false,
      avatarError: false,
      profile: profileState
    }
  },
  created(){
    this.fetchUserProfile()
  },
  computed:{
    nickname(){
      return this.profile.nickname || '用户'
    },
    avatar(){
      return this.profile.avatar || ''
    },
    /**
     * 实际塞进 <img src> 的地址。
     * 又拍云只提供 http，而站点是全站 https，直接用原地址会被浏览器按混合内容拦掉，
     * 所以统一走 resolveAvatarUrl() 换成后端同源 https 代理地址。
     */
    avatarSrc(){
      return resolveAvatarUrl(this.avatar)
    },
    nicknameFirst(){
      const name = this.nickname || '用'
      return name.charAt(0)
    }
  },
  watch:{
    // 头像地址变了（上传成功 / 拉到服务端最新资料）就要重新允许加载，
    // 否则上一次加载失败留下的 avatarError=true 会让头像永久退回首字母。
    avatarSrc(){
      this.avatarError = false
    }
  },
  methods:{
    async fetchUserProfile(){
      try {
        const res = await getMe()
        if (res && res.success && res.data) {
          this.profile.nickname = res.data.nickname || this.profile.nickname
          // 以服务端为准：服务端明确返回空（用户清了头像）时也要把本地缓存清掉，
          // 只有字段缺失（未返回该字段）时才保留本地值，避免误清空。
          if (res.data.avatarUrl !== undefined) {
            this.profile.avatar = res.data.avatarUrl || ''
          }
          saveProfile(this.profile)
          syncUserLocalStorage(this.profile)
        }
      } catch (e) {
        // 未登录或网络异常时静默使用本地缓存
      }
    },
    onClick(key){
      if(key!==this.active){
        this.$emit('change',key)
      }
    },
    goBack(){
      this.$confirm('确定要返回首页吗？未保存的编辑内容可能会丢失。', '确认返回', {
        confirmButtonText: '确定返回',
        cancelButtonText: '取消',
        type: 'warning',
        closeOnClickModal: false,
        closeOnPressEscape: false
      }).then(() => {
        router.go('/')
      }).catch(() => {})
    },
    openMenu(){
      if (!this.menuOpen) {
        this.menuOpen = true
      }
    },
    goProfile(){
      this.menuOpen = false
      router.go('/taskblock/profile')
    },
    logout(){
      this.menuOpen = false
      // 先注销服务端 token（清 Redis 登录态），再清本地缓存。
      // 失败静默：网络异常也不该阻塞用户退出。
      logoutApi().catch(() => {})
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('taskblock-profile')
      // 清掉内存里的共享状态，否则同一会话换账号登录时会先闪一下上一个用户的头像 / 壁纸
      resetProfileState()
      router.go('/')
    },
    onAvatarClick(){
      this.$confirm('是否上传新头像？', '上传头像', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'info'
      }).then(() => {
        this.$refs.avatarInput.click()
      }).catch(() => {})
    },
    async onAvatarChange(e){
      const file = e.target.files[0]
      if (!file) return
      try {
        const uploadRes = await uploadAvatar(file)
        if (!uploadRes || !uploadRes.success || !uploadRes.data) {
          this.$message.error((uploadRes && uploadRes.message) || '头像上传失败')
          return
        }
        const updateRes = await updateAvatar(uploadRes.data)
        if (updateRes && updateRes.success) {
          this.profile.avatar = uploadRes.data
          saveProfile(this.profile)
          syncUserLocalStorage(this.profile)
          this.$message.success('头像已更新')
        } else {
          this.$message.error((updateRes && updateRes.message) || '头像保存失败')
        }
      } catch (err) {
        this.$message.error('头像上传失败')
      }
      e.target.value = ''
    }
  }
}
</script>


<style scoped>

.task-nav{
  height:72px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  padding:0 28px;
  background:var(--tb-nav-bg, #5b9147);
  border-radius:0;
  box-shadow:0 8px 20px rgba(50,90,40,.18);
  flex-shrink:0;
  position:relative;
  z-index:100;
}


/* 左侧导航 */
.nav-panel{
  flex:1;
  display:flex;
  align-items:center;
  gap:10px;
}


.nav-item{
  height:46px;
  padding:0 28px;
  display:flex;
  align-items:center;
  justify-content:center;
  border-radius:12px;
  color:#23421b;
  background:var(--tb-nav-item-bg, #86c765);
  font-size:16px;
  font-weight:600;
  cursor:pointer;
  transition:.25s;
}


.nav-item:hover{
  background:var(--tb-nav-item-hover, #97d776);
  transform:translateY(-1px);
}


.nav-item.is-active{
  background:var(--tb-nav-active-bg, #3f7334);
  color:#ffffff;
  box-shadow:0 5px 12px rgba(30,70,20,.25);
}



/* 右侧 */
.nav-right{
  display:flex;
  align-items:center;
  gap:12px;
}


/* 返回 */
.back-btn{
  width:46px;
  height:46px;
  display:flex;
  align-items:center;
  justify-content:center;
  border-radius:50%;
  background:#eef6e8;
  color:#426735;
  cursor:pointer;
  transition:.25s;
}


.back-btn:hover{
  background:#ffffff;
}


.arrow{
  font-size:22px;
}



/* 用户 */
.user-menu{
  position:relative;
  display:flex;
  align-items:center;
}

.user-box{
  width:46px;
  height:46px;
  display:flex;
  align-items:center;
  justify-content:center;
  border-radius:50%;
  background:transparent;
  cursor:pointer;
  transition:transform .2s, box-shadow .2s;
}

.user-box:hover{
  transform:translateY(-1px);
  box-shadow:0 4px 10px rgba(0,0,0,.2);
}


.nav-avatar{
  width:42px;
  height:42px;
  display:flex;
  align-items:center;
  justify-content:center;
  border-radius:50%;
  background:#1a1a1a;
  color:#fff;
  font-size:16px;
  font-weight:600;
  object-fit:cover;
}

.avatar-wrap{
  position:relative;
  width:42px;
  height:42px;
  border-radius:50%;
  cursor:pointer;
  overflow:hidden;
}

.avatar-mask{
  position:absolute;
  inset:0;
  display:flex;
  align-items:center;
  justify-content:center;
  background:rgba(0,0,0,.55);
  color:#fff;
  font-size:12px;
  font-weight:600;
  text-align:center;
  line-height:1.2;
  padding:4px;
  opacity:0;
  transition:opacity .2s;
}

.avatar-wrap:hover .avatar-mask{
  opacity:1;
}


/* 下拉菜单 */
.dropdown{
  position:absolute;
  top:calc(100% + 10px);
  right:0;
  min-width:140px;
  background:#fff;
  border:1px solid #e8e8e8;
  border-radius:10px;
  box-shadow:0 8px 24px rgba(0,0,0,.12);
  padding:6px;
  opacity:0;
  transform:translateY(-6px);
  pointer-events:none;
  transition:opacity .2s, transform .2s;
  z-index:200;
}

.dropdown::before{
  content:"";
  position:absolute;
  top:-10px;
  left:0;
  right:0;
  height:10px;
}

.dropdown.open,
.user-menu:hover .dropdown{
  opacity:1;
  transform:translateY(0);
  pointer-events:auto;
}

.dropdown-item{
  padding:10px 14px;
  border-radius:8px;
  color:#333;
  font-size:13px;
  font-weight:500;
  text-align:left;
  cursor:pointer;
  transition:.2s;
}

.dropdown-item:hover{
  background:#f5f5f5;
}

.dropdown-item.logout{
  color:#e04e4e;
}

.dropdown-item.logout:hover{
  background:#fff0f0;
}



/* 移动端 */
@media(max-width:700px){

  .task-nav{
    padding:0 12px;
  }

  .nav-item{
    padding:0 14px;
  }

  .user-box span{
    display:none;
  }

  .back-btn{
    padding:0 12px;
  }

  .dropdown{
    min-width:110px;
    right:-6px;
  }

}

/* ===== 轻量扁平主题覆盖（参考稿配色）=====
   只改颜色 / 描边 / 圆角 / 阴影，尺寸与间距沿用上面的原值。
   导航主色仍取用户在个人中心设置的主题色（--tb-primary），只是不再整条压成深绿。 */
.task-nav{
  background:var(--lp-surface,#fff);
  border-bottom:2px solid var(--lp-border,#e3efe6);
  box-shadow:none;
}
.nav-item{
  border-radius:14px;
  color:var(--lp-text-2,#5f7268);
  background:transparent;
  font-weight:500;
}
.nav-item:hover{
  background:var(--lp-primary-soft,#e9f5ec);
  color:var(--lp-primary-dark,#2f7a4a);
  transform:none;
}
.nav-item.is-active{
  background:var(--tb-primary,#4aa46a);
  color:#fff;
  box-shadow:none;
}
.back-btn{
  background:var(--lp-surface-2,#f7fbf8);
  color:var(--lp-text-2,#5f7268);
}
.back-btn:hover{
  background:var(--lp-primary-soft,#e9f5ec);
  color:var(--lp-primary-dark,#2f7a4a);
}
.nav-avatar{
  background:var(--lp-primary-dark,#2f7a4a);
}
.dropdown{
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.75rem;
  box-shadow:0 8px 24px rgba(52,102,68,.12);
}
.dropdown-item{
  color:var(--lp-text,#26332b);
}
.dropdown-item:hover{
  background:var(--lp-surface-2,#f7fbf8);
}
</style>
